// src/pages/properties/PropertiesPage.tsx
import {useState, useCallback} from "react";
import {useNavigate} from "react-router-dom";
import {
  Plus,
  Eye,
  Image,
  MapPin,
  Calendar,
  DollarSign,
  Home,
  Building,
  CheckCircle,
  XCircle,
} from "lucide-react";
import {DataTable, PageHeader} from "../../components/common";
import {Button, Card, Badge, Modal} from "../../components/ui";
import {useProperties} from "../../hooks/useProperties";
import {useTable} from "../../hooks/common/useTable";
import {useModal} from "../../hooks/common/useModal";
import {
  createCommonActions,
  createCommonBulkActions,
} from "../../components/common/DataTable/TableActions";
import type {
  DataTableColumn,
  DataTableAction,
  DataTableBulkAction,
} from "../../components/common/DataTable/DataTableTypes";
import type {PropertyResponseDto} from "../../types/property";
import {formatCurrency, formatDate, formatNumber} from "../../utils/formatters";
import {ROUTES} from "../../utils/constants";
import PropertyPhotoGallery from "./components/PropertyPhotoGallery";

const PropertiesPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedProperty, setSelectedProperty] =
    useState<PropertyResponseDto | null>(null);

  // Modal hooks
  const photoGalleryModal = useModal("photo-gallery");

  // Table state management
  const {
    currentPage,
    pageSize,
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleFilterChange,
  } = useTable({
    key: "properties",
    defaultPageSize: 25,
  });

  // Debug logging
  console.log('PropertiesPage - Current Page:', currentPage);
  console.log('PropertiesPage - Query Params:', queryParams);

  // Data fetching
  const {
    properties,
    pagination,
    stats,
    isLoading,
    error,
    handleDelete,
    handleBulkDelete,
    handleStatusChange,
  } = useProperties(queryParams);

  // Memoize callbacks to prevent unnecessary re-renders
  const handleSearchChange = useCallback((value: string) => {
    handleFilterChange("search", value);
  }, [handleFilterChange]);

  const handleFiltersChange = useCallback((filters: Record<string, unknown>) => {
    Object.entries(filters).forEach(([key, value]) => {
      handleFilterChange(key, value);
    });
  }, [handleFilterChange]);

  // Table columns configuration
  const columns: DataTableColumn<PropertyResponseDto & Record<string, any>>[] =
    [
      {
        key: "primaryPhoto",
        title: "Photo",
        width: 80,
        render: (_, record) => (
          <div className="w-16 h-12 bg-gray-100 dark:bg-dark-700 rounded-lg overflow-hidden">
            {record.primaryPhoto ? (
              <img
                src={record.primaryPhoto.photoUrl}
                alt={record.titleEng}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Image className="w-6 h-6 text-gray-400" />
              </div>
            )}
          </div>
        ),
      },
      {
        key: "uniqueId",
        title: "Property ID",
        sortable: true,
        width: 120,
        render: (_, record) => (
          <span className="font-mono text-sm text-primary-600 dark:text-primary-400">
            {record.uniqueId}
          </span>
        ),
      },
      {
        key: "titleEng",
        title: "Title",
        sortable: true,
        render: (_, record) => (
          <div className="space-y-1">
            <p className="font-medium text-gray-900 dark:text-white line-clamp-1">
              {record.titleEng}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
              {record.titleMm}
            </p>
          </div>
        ),
      },
      {
        key: "price",
        title: "Price",
        sortable: true,
        width: 120,
        align: "right",
        render: (value, record) => (
          <div className="text-right">
            <p className="font-semibold text-gray-900 dark:text-white">
              {formatCurrency(value as number)}
            </p>
            <Badge
              variant={record.listingType === "sale" ? "success" : "info"}
              size="sm"
            >
              {record.listingType === "sale" ? "Sale" : "Rent"}
            </Badge>
          </div>
        ),
      },
      {
        key: "location",
        title: "Location",
        render: (_, record) => (
          <div className="space-y-1">
            <div className="flex items-center text-sm text-gray-900 dark:text-white">
              <MapPin className="w-3 h-3 mr-1 text-gray-400" />
              {record.township.nameEng}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {record.region.nameEng}
            </p>
          </div>
        ),
      },
      {
        key: "propertyType",
        title: "Type",
        render: (_, record) => (
          <div className="flex items-center">
            {record.propertyType.icon && (
              <span className="mr-2">{record.propertyType.icon}</span>
            )}
            <span className="text-sm text-gray-900 dark:text-white">
              {record.propertyType.nameEng}
            </span>
          </div>
        ),
      },
      {
        key: "details",
        title: "Details",
        render: (_, record) => (
          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center">
              <Home className="w-3 h-3 mr-1" />
              {record.bedrooms}
            </div>
            <div className="flex items-center">
              <Building className="w-3 h-3 mr-1" />
              {record.bathrooms}
            </div>
            {record.sizeArea && (
              <span className="text-xs">{record.sizeArea}</span>
            )}
          </div>
        ),
      },
      {
        key: "status",
        title: "Status",
        sortable: true,
        width: 120,
        render: (value, record) => {
          const getStatusBadge = (status: string) => {
            switch (status) {
              case "active":
                return <Badge variant="success">Active</Badge>;
              case "sold":
                return <Badge variant="gray">Sold</Badge>;
              case "rented":
                return <Badge variant="info">Rented</Badge>;
              case "inactive":
                return <Badge variant="danger">Inactive</Badge>;
              default:
                return <Badge variant="gray">{status}</Badge>;
            }
          };

          return (
            <div className="space-y-1">
              {getStatusBadge(value as string)}
              <div className="flex items-center text-xs text-gray-500">
                <Eye className="w-3 h-3 mr-1" />
                {formatNumber(record.reads)} views
              </div>
            </div>
          );
        },
      },
      {
        key: "photos",
        title: "Photos",
        width: 80,
        render: (_, record) => (
          <button
            onClick={() => {
              setSelectedProperty(record);
              photoGalleryModal.open();
            }}
            className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200 bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/20 dark:hover:bg-primary-900/30 rounded-md transition-colors"
          >
            <Image className="w-3 h-3 mr-1" />
            {record.photoCount}
          </button>
        ),
      },
      {
        key: "createdAt",
        title: "Created",
        sortable: true,
        width: 120,
        render: (value) => (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center">
              <Calendar className="w-3 h-3 mr-1" />
              {formatDate(value as string, "short")}
            </div>
          </div>
        ),
      },
    ];

  // Table actions
  const actions: DataTableAction<PropertyResponseDto>[] = [
    createCommonActions<PropertyResponseDto>().view((record) => {
      navigate(ROUTES.PROPERTY_DETAIL(record.id));
    }),
    createCommonActions<PropertyResponseDto>().edit((record) => {
      navigate(ROUTES.PROPERTY_EDIT(record.id));
    }),
    {
      key: "photos",
      label: "Photos",
      icon: <Image className="w-4 h-4" />,
      onClick: (record) => {
        setSelectedProperty(record);
        photoGalleryModal.open();
      },
      variant: "ghost",
    },
    {
      key: "deactivate",
      label: "Deactivate",
      icon: <XCircle className="w-4 h-4" />,
      onClick: (record) => handleStatusChange(record.id, "inactive"),
      variant: "ghost",
      show: (record) => record.status === "active",
    },
    {
      key: "activate",
      label: "Activate",
      icon: <CheckCircle className="w-4 h-4" />,
      onClick: (record) => handleStatusChange(record.id, "active"),
      variant: "ghost",
      show: (record) => record.status === "inactive",
    },

    createCommonActions<PropertyResponseDto>().delete((record) => {
      handleDelete(record.id);
    }),
  ];

  // Bulk actions
  const bulkActions: DataTableBulkAction<PropertyResponseDto>[] = [
    {
      key: "activate",
      label: "Activate",
      icon: <CheckCircle className="w-4 h-4" />,
      onClick: (records) => {
        // Implement bulk activate
        console.log("Bulk activate:", records);
      },
      variant: "secondary",
    },
    {
      key: "deactivate",
      label: "Deactivate",
      icon: <XCircle className="w-4 h-4" />,
      onClick: (records) => {
        // Implement bulk deactivate
        console.log("Bulk deactivate:", records);
      },
      variant: "secondary",
    },
    createCommonBulkActions<PropertyResponseDto>().bulkDelete((records) => {
      const propertyIds = records.map((r) => r.id);
      handleBulkDelete(propertyIds);
    }),
  ];

  // Filter configuration with Price Range
  const filterConfig = [
    {
      key: "search",
      label: "Search",
      type: "text" as const,
      placeholder: "Search properties...",
    },
    {
      key: "status",
      label: "Status",
      type: "select" as const,
      options: [
        {value: "", label: "All Status"},
        {value: "active", label: "Active"},
        {value: "sold", label: "Sold"},
        {value: "rented", label: "Rented"},
        {value: "inactive", label: "Inactive"},
      ],
    },
    {
      key: "listingType",
      label: "Listing Type",
      type: "select" as const,
      options: [
        {value: "", label: "All Types"},
        {value: "sale", label: "For Sale"},
        {value: "rent", label: "For Rent"},
      ],
    },
    {
      key: "finishStatus",
      label: "Finish Status",
      type: "select" as const,
      options: [
        {value: "", label: "All Finish Status"},
        {value: "not_finished", label: "Not Finished"},
        {value: "partly_finished", label: "Partly Finished"},
        {value: "totally_finished", label: "Totally Finished"},
      ],
    },
    // Price Range Filters
    {
      key: "minPrice",
      label: "Min Price",
      type: "number" as const,
      placeholder: "Minimum price",
    },
    {
      key: "maxPrice",
      label: "Max Price",
      type: "number" as const,
      placeholder: "Maximum price",
    },
    // Quick Price Range Options
    {
      key: "priceRange",
      label: "Price Range",
      type: "select" as const,
      options: [
        {value: "", label: "Any Price"},
        {value: "0-1000000", label: "Under 10 Lakhs"},
        {value: "1000000-2500000", label: "10 - 25 Lakhs"},
        {value: "2500000-5000000", label: "25 - 50 Lakhs"},
        {value: "5000000-10000000", label: "50 Lakhs - 1 Crore"},
        {value: "10000000-25000000", label: "1 - 2.5 Crores"},
        {value: "25000000-", label: "Above 2.5 Crores"},
      ],
      onChange: (value: string) => {
        if (!value) {
          handleFilterChange("minPrice", "");
          handleFilterChange("maxPrice", "");
          return;
        }

        const [min, max] = value.split("-");
        handleFilterChange("minPrice", min || "");
        handleFilterChange("maxPrice", max || "");
      },
    },
    // Additional Room Filters
    {
      key: "bedrooms",
      label: "Min Bedrooms",
      type: "select" as const,
      options: [
        {value: "", label: "Any"},
        {value: "1", label: "1+"},
        {value: "2", label: "2+"},
        {value: "3", label: "3+"},
        {value: "4", label: "4+"},
        {value: "5", label: "5+"},
      ],
    },
    {
      key: "bathrooms",
      label: "Min Bathrooms",
      type: "select" as const,
      options: [
        {value: "", label: "Any"},
        {value: "1", label: "1+"},
        {value: "2", label: "2+"},
        {value: "3", label: "3+"},
        {value: "4", label: "4+"},
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Properties"
        breadcrumbs={[{label: "Dashboard", path: "/"}, {label: "Properties"}]}
        action={{
          label: "Add Property",
          onClick: () => navigate(ROUTES.PROPERTY_CREATE),
          icon: <Plus className="w-4 h-4 mr-2" />,
        }}
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Properties
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(stats.totalProperties)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <Building className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Active Properties
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(stats.activeProperties)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  For Sale
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(stats.saleProperties)}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Average Price
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(stats.averagePrice)}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">
                Unable to Load Properties
              </h3>
              <p className="text-sm text-red-600 dark:text-red-300">
                {(() => {
                  if ('status' in error && error.status === 401) {
                    return "Please log in again to access the properties.";
                  }
                  if ('status' in error && error.status === 'FETCH_ERROR') {
                    return "Network error. Please check your connection and try again.";
                  }
                  if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
                    return `Error: ${(error.data as any).message}`;
                  }
                  return 'Failed to load properties';
                })()}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Price Range Info Section */}
      {/* <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Filter by Price Range
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Use custom min/max price or select from predefined ranges
              </p>
            </div>
          </div>
          <div className="text-right">
            {filters.minPrice || filters.maxPrice ? (
              <div className="text-sm">
                <p className="text-gray-600 dark:text-gray-400">
                  Current Range:
                </p>
                <p className="font-semibold text-blue-600 dark:text-blue-400">
                  {filters.minPrice
                    ? formatCurrency(Number(filters.minPrice))
                    : "Any"}{" "}
                  -{" "}
                  {filters.maxPrice
                    ? formatCurrency(Number(filters.maxPrice))
                    : "Any"}
                </p>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                All price ranges
              </div>
            )}
          </div>
        </div>
      </Card> */}

      {/* Data Table */}
      <Card>
        {!error && !isLoading && (!properties || properties.length === 0) && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Building className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Properties Found
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Get started by adding your first property.
            </p>
            <Button
              onClick={() => navigate(ROUTES.PROPERTY_CREATE)}
              className="mx-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          </div>
        )}
        
        {(properties && properties.length > 0) && (
          <DataTable
            data={properties}
            columns={columns}
            loading={isLoading}
            rowKey="id"
            actions={actions}
            bulkActions={bulkActions}
            selection={{type: "checkbox"}}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: pagination?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              onChange: (page: number, newPageSize: number) => {
                console.log('Pagination onChange called:', { page, newPageSize, currentPage, pageSize });
                // Only update page size if it actually changed
                if (newPageSize !== pageSize) {
                  handlePageSizeChange(newPageSize);
                } else {
                  // Only update page if page size didn't change
                  handlePageChange(page);
                }
              },
            }}
            filters={filterConfig}
            onFilter={handleFiltersChange}
            onSort={(sort) => {
              if (sort) {
                handleSort(sort.field);
              }
            }}
            searchable
            searchPlaceholder="Search properties..."
            onSearch={handleSearchChange}
            striped
            hoverable
          />
        )}
      </Card>

      {/* Photo Gallery Modal */}
      <Modal
        isOpen={photoGalleryModal.isOpen}
        onClose={photoGalleryModal.close}
        title={`Photo Gallery - ${selectedProperty?.titleEng || ""}`}
        size="xl"
      >
        {selectedProperty && (
          <PropertyPhotoGallery
            property={selectedProperty}
            onClose={photoGalleryModal.close}
          />
        )}
      </Modal>
    </div>
  );
};

export default PropertiesPage;
